
































































































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { ChecklistApi } from '@/services/ChecklistApi';
import { BaseMixin } from '@/mixins/BaseMixin';
import { FormMixin } from '@/mixins/FormMixin';
import {
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  SquareIcon,
  FileTextIcon,
  EditIcon,
  Trash2Icon,
  XIcon,
} from 'vue-feather-icons';
import {
  ChecklistModel,
  IChecklistItem,
  CreateChecklistDto,
  UpdateChecklistDto,
  IDamage,
} from 'admiral-core';
import ChecklistEditModal from './ChecklistEditModal.vue';

type ChecklistEditData = {
  mode: 'item' | 'item-edit' | 'title' | 'create';
  checklistId?: string;
  currentValue: string;
  checklist?: any;
  itemId?: string;
  damageId?: string;
  taskId?: string;
};

type ChecklistVM = Partial<ChecklistModel> & { items: Array<Partial<IChecklistItem>> };

@Component({
  components: {
    PlusIcon,
    FileTextIcon,
    SquareIcon,
    CheckSquareIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    EditIcon,
    Trash2Icon,
    XIcon,
    ChecklistEditModal,
  },
})
export default class Checklist extends mixins(BaseMixin, FormMixin) {
  @Prop({ type: String, required: false }) readonly damageId?: string;
  @Prop({ type: String, required: false }) readonly taskId?: string;

  lists: ChecklistVM[] = [];
  collapsed: boolean[] = [];
  checklistToDelete: ChecklistVM | null = null;
  itemToDelete: { checklist: ChecklistVM; item: Partial<IChecklistItem> } | null = null;
  checklistToSaveAsTemplate: ChecklistVM | null = null;
  templateToDelete: ChecklistVM | null = null;
  editModalData: ChecklistEditData | null = null;

  // Template popup state
  showTemplatePopup = false;
  templates: ChecklistVM[] = [];
  isLoadingTemplates = false;
  templatePopupStyle = {};

  async mounted() {
    await this.loadChecklists();
  }

  @Watch('damageId')
  async onDamageIdChanged() {
    await this.loadChecklists();
  }

  @Watch('taskId')
  async onTaskIdChanged() {
    await this.loadChecklists();
  }

  async loadChecklists() {
    try {
      if (!this.agency || (!this.damageId && !this.taskId)) return;

      const { data } = await ChecklistApi.getChecklists(this.agency, this.damageId, this.taskId);
      this.lists = (data || []).map((checklist) => ({
        ...checklist,
        items: checklist.items || [],
      }));
      this.collapsed = this.lists.map(() => false);
    } catch (error) {
      console.error('Fehler beim Laden der Checklisten:', error);
      this.$toastr.e('Fehler beim Laden der Checklisten');
    }
  }

  toggleCollapse(index: number) {
    this.$set(this.collapsed, index, !this.collapsed[index]);
  }

  async toggleItem(list: ChecklistVM, item: Partial<IChecklistItem>) {
    if (!list.id || !item.id) return;

    try {
      const updatedItems = (list.items || []).map((listItem) =>
        listItem.id === item.id ? { ...listItem, done: !listItem.done } : listItem,
      );

      const checklistIndex = this.lists.findIndex((checklist) => checklist.id === list.id);
      if (checklistIndex !== -1) {
        this.$set(this.lists, checklistIndex, {
          ...this.lists[checklistIndex],
          items: updatedItems,
        });
      }

      const updateDto: UpdateChecklistDto = {
        agency: this.agency,
        title: list.title || '',
        items: updatedItems,
      };

      await ChecklistApi.update(list.id, updateDto);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Items:', error);
      this.$toastr.e('Fehler beim Aktualisieren der Aufgabe');

      await this.loadChecklists();
    }
  }

  async addTask(list: ChecklistVM) {
    if (!list.id) return;

    this.editModalData = {
      mode: 'item',
      checklistId: list.id,
      currentValue: '',
      checklist: list,
    };

    this.$bvModal.show('checklist-edit-modal');
  }

  editTitle(list: ChecklistVM) {
    if (!list.id) return;

    this.editModalData = {
      mode: 'title',
      checklistId: list.id,
      currentValue: list.title || '',
      checklist: list,
    };

    this.$bvModal.show('checklist-edit-modal');
  }

  editItem(list: ChecklistVM, item: Partial<IChecklistItem>) {
    if (!list.id || !item.id) return;

    this.editModalData = {
      mode: 'item-edit',
      checklistId: list.id,
      currentValue: item.text || '',
      checklist: list,
      itemId: item.id,
    };

    this.$bvModal.show('checklist-edit-modal');
  }

  openSaveAsTemplateModal(checklist: ChecklistVM) {
    this.checklistToSaveAsTemplate = checklist;
    this.$bvModal.show('modal-save-as-template');
  }

  async confirmTemplateSave() {
    if (!this.checklistToSaveAsTemplate) return;

    try {
      this.isLoading = true;

      const checklist = this.checklistToSaveAsTemplate;

      const createDto: CreateChecklistDto = {
        agency: this.agency,
        title: checklist.title || '',
        items: (checklist.items || []).map((item) => ({
          ...item,
          done: false,
        })),
        isTemplate: true,
        damage: undefined,
      };

      await ChecklistApi.create(createDto);

      this.$bvModal.hide('modal-save-as-template');

      this.$toastr.s('Vorlage erfolgreich gespeichert');
    } catch (error) {
      console.error('Fehler beim Speichern der Vorlage:', error);
      this.$toastr.e('Fehler beim Speichern der Vorlage');
    } finally {
      this.isLoading = false;
      this.checklistToSaveAsTemplate = null;
    }
  }

  async openTemplatePopup() {
    await this.loadTemplates();
    this.positionTemplatePopup();
    this.showTemplatePopup = true;
  }

  closeTemplatePopup() {
    this.showTemplatePopup = false;
    this.templates = [];
  }

  async loadTemplates() {
    if (!this.agency) return;

    try {
      this.isLoadingTemplates = true;

      const { data } = await ChecklistApi.getTemplates(this.agency);

      this.templates = data || [];
    } catch (error) {
      console.error('Fehler beim Laden der Vorlagen:', error);
      this.$toastr.e('Fehler beim Laden der Vorlagen');
      this.templates = [];
    } finally {
      this.isLoadingTemplates = false;
    }
  }

  positionTemplatePopup() {
    this.$nextTick(() => {
      const button = this.$refs.templateButton as HTMLElement;
      if (button) {
        const rect = button.getBoundingClientRect();
        // Position popup above the button
        this.templatePopupStyle = {
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + 8}px`,
          left: `${rect.left}px`,
          zIndex: 1050,
        };
      }
    });
  }

  async selectTemplate(template: ChecklistVM) {
    if (!template || !template.title) return;

    try {
      this.isLoading = true;

      const createDto: CreateChecklistDto = {
        agency: this.agency,
        title: template.title,
        items: (template.items || []).map((item) => ({
          ...item,
          done: false,
        })),
        isTemplate: false,
        damage: this.damageId ? ({ id: this.damageId } as IDamage) : undefined,
        task: this.taskId ? { id: this.taskId } : undefined,
      };

      const { data } = await ChecklistApi.create(createDto);
      this.closeTemplatePopup();

      this.lists = [...this.lists, { ...data, items: data.items || [] }];
      this.$set(this.collapsed, this.lists.length - 1, false);

      this.$toastr.s('Checkliste aus Vorlage erstellt');
    } catch (error) {
      console.error('Fehler beim Erstellen der Checkliste aus Vorlage:', error);
      this.$toastr.e('Fehler beim Erstellen der Checkliste aus Vorlage');
    } finally {
      this.isLoading = false;
    }
  }

  getTaskLabel(items: any[]): string {
    const count = (items && items.length) || 0;
    return count === 1 ? 'Aufgabe' : 'Aufgaben';
  }

  openDeleteItemModal(checklist: ChecklistVM, item: Partial<IChecklistItem>) {
    this.itemToDelete = { checklist, item };
    this.$bvModal.show('modal-delete-item');
  }

  async confirmItemDeletion() {
    if (!this.itemToDelete || !this.itemToDelete.checklist.id || !this.itemToDelete.item.id) return;

    try {
      this.isLoading = true;

      const checklist = this.itemToDelete.checklist;
      const checklistId = checklist.id;
      const itemToDeleteId = this.itemToDelete.item.id;
      if (!checklistId) return;

      const updatedItems = (checklist.items || []).filter((item) => item.id !== itemToDeleteId);

      const updateDto: UpdateChecklistDto = {
        agency: this.agency,
        title: checklist.title || '',
        items: updatedItems,
      };

      await ChecklistApi.update(checklistId, updateDto);

      const checklistIndex = this.lists.findIndex((list) => list.id === checklistId);
      if (checklistIndex !== -1) {
        this.$set(this.lists, checklistIndex, {
          ...this.lists[checklistIndex],
          items: updatedItems,
        });
      }
      this.$bvModal.hide('modal-delete-item');

      this.$toastr.s('Aufgabe erfolgreich gelöscht');
    } catch (error) {
      console.error('Fehler beim Löschen der Aufgabe:', error);
      this.$toastr.e('Fehler beim Löschen der Aufgabe');
    } finally {
      this.isLoading = false;
      this.itemToDelete = null;
    }
  }

  openCreateChecklistModal() {
    this.editModalData = {
      mode: 'create',
      currentValue: '',
      damageId: this.damageId,
      taskId: this.taskId,
    };

    this.$bvModal.show('checklist-edit-modal');
  }

  openDeleteModal(checklist: ChecklistVM) {
    this.checklistToDelete = checklist;
    this.$bvModal.show('modal-delete-checklist');
  }

  async confirmDeletion() {
    if (!this.checklistToDelete || !this.checklistToDelete.id) return;

    try {
      this.isLoading = true;

      await ChecklistApi.delete(this.checklistToDelete.id);
      this.$bvModal.hide('modal-delete-checklist');

      await this.loadChecklists();

      this.$toastr.s('Checkliste erfolgreich gelöscht');
    } catch (error) {
      console.error('Fehler beim Löschen der Checkliste:', error);
      this.$toastr.e('Fehler beim Löschen der Checkliste');
    } finally {
      this.isLoading = false;
      this.checklistToDelete = null;
    }
  }

  onChecklistCreated(event: { checklist: ChecklistVM }) {
    this.lists = [...this.lists, event.checklist];
    this.$set(this.collapsed, this.lists.length - 1, false);
  }

  onItemAdded(event: {
    checklistId: string;
    newItem: IChecklistItem;
    updatedItems: IChecklistItem[];
  }) {
    const checklistIndex = this.lists.findIndex((list) => list.id === event.checklistId);
    if (checklistIndex !== -1) {
      this.$set(this.lists, checklistIndex, {
        ...this.lists[checklistIndex],
        items: event.updatedItems,
      });
    }
  }

  onItemUpdated(event: {
    checklistId: string;
    itemId: string;
    newText: string;
    updatedItems: IChecklistItem[];
  }) {
    const checklistIndex = this.lists.findIndex((list) => list.id === event.checklistId);
    if (checklistIndex !== -1) {
      this.$set(this.lists, checklistIndex, {
        ...this.lists[checklistIndex],
        items: event.updatedItems,
      });
    }
  }

  onTitleUpdated(event: { checklistId: string; newTitle: string }) {
    const checklistIndex = this.lists.findIndex((list) => list.id === event.checklistId);
    if (checklistIndex !== -1) {
      this.$set(this.lists, checklistIndex, {
        ...this.lists[checklistIndex],
        title: event.newTitle,
      });
    }
  }

  openDeleteTemplateModal(template: ChecklistVM) {
    this.templateToDelete = template;
    this.$bvModal.show('modal-delete-template');
  }

  async confirmTemplateDeletion() {
    if (!this.templateToDelete?.id) return;

    try {
      this.isLoading = true;
      await ChecklistApi.delete(this.templateToDelete.id);

      this.templates = this.templates.filter((t) => t.id !== this.templateToDelete!.id);

      this.$toastr.s('Vorlage erfolgreich gelöscht');
      this.$bvModal.hide('modal-delete-template');
      this.templateToDelete = null;
    } catch (error) {
      console.error('Fehler beim Löschen der Vorlage:', error);
      this.$toastr.e('Fehler beim Löschen der Vorlage');
    } finally {
      this.isLoading = false;
    }
  }
}
